import { EventBus } from '../EventBus.js';
import { pedirEspera, soltarEspera } from './esperaGlobal.js';
import { alDejarDeMandar } from './soloMandaLaDeDelante.js';

/**
 * **El preloader global mientras se espera algo, atado a la SESIÓN y no solo a la respuesta.**
 *
 * El preloader de Framework7 no es de la pantalla que lo enciende: es un modal de la APLICACIÓN y
 * tapa lo que haya debajo, incluido el formulario de acceso. Una pantalla que lo pone antes de
 * pedir algo y lo quita en el `then` de esa petición está apostando a que la petición termine, y
 * eso no siempre pasa: cerrar sesión hace que `removeSession()` navegue al login **sin mirar qué
 * modales hay puestos**, así que si aquella petición va lenta o se queda colgada lo que queda
 * delante es el formulario de acceso con un preloader encima y nada que pulsar (revisión CODEX del
 * PR #1735, ronda 2, sobre `cargar()` de «Mis vacaciones»).
 *
 * ## Por qué vive aquí y no en cada pantalla
 *
 * Porque son cuatro sitios del módulo de vacaciones los que encienden el preloader esperando al
 * servidor —el resumen de «Mis vacaciones», el envío de una solicitud y, por `accionDeLaSesion`,
 * las cinco acciones de aprobar, denegar y anular— y el descuido es el mismo en todos: el cierre
 * cuelga de que la petición acabe. Repartido por llamante se olvida en el siguiente que se
 * escriba. Es la misma decisión que ya se tomó con el sello de sesión del carril
 * (`carrilPeticiones`, #262) y con el de los diálogos (`accionDeLaSesion`, #59): un solo
 * vigilante, sin junturas donde equivocarse.
 *
 * ## Las tres garantías, y las tres hacen falta
 *
 * - **Se cierra si la sesión se va**, por los avisos `app:session:close`/`app:session:open`, que
 *   es lo que no hacía nadie. La suscripción dura exactamente lo que el preloader está puesto, así
 *   que no deja oyentes sueltos ni depende de que el componente se destruya —el de «Mis
 *   vacaciones» no se destruye: su ruta lleva `keepAlive`—.
 *
 *   [#59, 17/10/2026] **Y si se sale de la pantalla**, que es el mismo caso con la sesión intacta
 *   y el que faltaba: las rutas del módulo son `keepAlive`, así que navegar a otra pantalla no
 *   destruye nada y la espera de una petición que se queda colgada seguía puesta —el modal es de
 *   la APLICACIÓN— encima de la pantalla de destino, sin nadie que pudiera apagarla. Las tres
 *   salidas no se enumeran aquí: se apunta el cierre en `soloMandaLaDeDelante`
 *   ({@link alDejarDeMandar}), que es el único sitio del módulo que las tiene. Es el contrato que
 *   fijó Donchurru el 17/10/2026 en la #59: relevar la espera, salir o cambiar de sesión suelta
 *   ese testigo, y solo ése.
 * - **Cerrar es idempotente y no cierra lo que no abrió.** `app.preloader.hide()` es global y no
 *   lleva cuenta de quién lo puso: un `hide()` de más apagaría el preloader que acabara de
 *   encender otro —la primera carga de la cuenta que entra, por ejemplo—. Con esto, el sitio donde
 *   la pantalla deja de esperar puede cerrarlo sin saber si la petición ya lo había cerrado.
 *
 *   [#59, 07/10/2026] Y «lo que no abrió» incluye **la espera de otro ayudante**, que es lo que
 *   faltaba: la guarda `puesto` es de esta instancia, así que dos de ellas solapadas llamaban las
 *   dos a `hide()` y la primera en acabar dejaba a la otra esperando sin indicador. Pasaba al
 *   anular —la acción contesta, arranca la recarga del resumen y el `then` final de la acción
 *   apagaba el preloader de esa recarga (revisión CODEX, ronda 4, `mis-vacaciones.vue:921`)— y en
 *   el mismo orden tras enviar una solicitud. Lo cierra `esperaGlobal`: este ayudante guarda **el
 *   testigo de su espera** y suelta ese y solo ese, así que el modal se apaga cuando no queda
 *   ninguno. Esta instancia sigue decidiendo **cuándo** suelta el suyo.
 *
 * Lo que **no** hace es cancelar la petición: eso no se puede desde aquí y tampoco hace falta. De
 * su respuesta ya se encargan el carril (`clave`/`deAhora`) y `accionDeLaSesion`, que la descartan
 * por no ser de esta sesión; lo que faltaba era el estado visual.
 *
 *   const preloader = preloaderDeLaSesion(this);   // en `created`, o al construir la acción
 *   preloader.mostrar();
 *   peticion.then(function() { preloader.cerrar(); });
 *
 * @param vm el componente que lo enciende; de él sale `$f7`
 */
export default function preloaderDeLaSesion(vm) {
  // El testigo de ESTA espera, o `null` si ahora mismo no hay ninguna puesta. Es lo que se suelta
  // y lo único que se puede soltar: la espera que haya pedido cualquier otro no se toca.
  //
  // La instancia de Framework7 viaja dentro del testigo (`esperaGlobal`) y se resuelve al
  // ENCENDERLO. No en la construcción, porque una pantalla lo construye en `created()` y ahí `$f7`
  // todavía puede no estar; y no al apagarlo, porque para entonces el componente puede estar ya
  // destruido —es justo el caso que esto viene a cubrir— y sin instancia no habría con qué cerrar
  // el modal.
  let testigo = null;
  // La baja del apunte de la salida de pantalla, o `null` si ahora mismo no hay ninguno puesto.
  // Se da de baja al cerrar por lo mismo que los dos oyentes del bus: un apunte que ya no tiene
  // nada que soltar engorda la lista de la pantalla con cada acción.
  let dejarDeApuntarLaSalida = null;

  function cerrar() {
    if (!testigo) return;
    const mio = testigo;
    testigo = null;
    EventBus.$off('app:session:close', cerrar);
    EventBus.$off('app:session:open', cerrar);
    if (dejarDeApuntarLaSalida) {
      dejarDeApuntarLaSalida();
      dejarDeApuntarLaSalida = null;
    }
    soltarEspera(mio);
  }

  return {
    mostrar: function() {
      if (testigo) return;
      // Los dos avisos, como las pantallas que vacían lo de la cuenta anterior: `close` es salir y
      // `open` es que ha entrado otra cuenta, y entre uno y otro puede no haber pasado nada más.
      EventBus.$on('app:session:close', cerrar);
      EventBus.$on('app:session:open', cerrar);
      // Y la salida de la pantalla, que con `keepAlive` no es ninguno de los dos.
      dejarDeApuntarLaSalida = alDejarDeMandar(vm, cerrar);
      testigo = pedirEspera(vm.$f7);
    },
    cerrar: cerrar,
    /** Si está puesto ahora mismo. Para quien tenga que decidir con eso, y para las pruebas. */
    puesto: function() { return testigo !== null; }
  };
}
